export const SimStateDonutChart = {
  mounted() {
    
    this.chart = null;
    this.initChart();
  },

  updated() {
    this.updateChart();
  },

  destroyed() {
    if (this.chart) {
      this.chart.destroy();
    }
  },

  initChart() {
    
    const activated = parseInt(this.el.dataset.activated) || 0;
    const blocked = parseInt(this.el.dataset.blocked) || 0;
    const delivered = parseInt(this.el.dataset.delivered) || 0;
    const terminated = parseInt(this.el.dataset.terminated) || 0;
    const other = parseInt(this.el.dataset.other) || 0;

    
    const total = activated + blocked + delivered + terminated + other;
    
    if (total === 0) {
      console.warn('⚠️ All data is zero! Chart will be empty.');
    }

    const ctx = this.el.getContext('2d');

    try {
      this.chart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Active', 'Blocked', 'Planned', 'Terminated', 'Other'],
          datasets: [{
            data: [activated, blocked, delivered, terminated, other],
            backgroundColor: [
              '#10B981',  // Green - Active
              '#EF4444',  // Red - Blocked
              '#3B82F6',  // Blue - Planned
              '#F97316',  // Orange - Terminated
              '#6B7280'   // Gray - Other
            ],
            borderColor: '#ffffff',
            borderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                padding: 15,
                font: {
                  size: 12,
                  weight: 'bold'
                },
                usePointStyle: true,
                pointStyle: 'circle',
                generateLabels: function(chart) {
                  const data = chart.data;
                  if (data.labels.length && data.datasets.length) {
                    const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                    
                    return data.labels.map((label, i) => {
                      const value = data.datasets[0].data[i];
                      const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                      
                      return {
                        text: `${label}: ${value} (${percentage}%)`,
                        fillStyle: data.datasets[0].backgroundColor[i],
                        hidden: false,
                        index: i
                      };
                    });
                  }
                  return [];
                }
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const label = context.label || '';
                  const value = context.parsed || 0;
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                  
                  if (label === 'Active') {
                    return `Active (Productive): ${value} (${percentage}%)`;
                  }
                  
                  if (label === 'Blocked') {
                    return `Blocked: ${value} (${percentage}%)`;
                  }
                  
                  if (label === 'Planned') {
                    return `Planned (Delivered): ${value} (${percentage}%)`;
                  }
                  
                  if (label === 'Terminated') {
                    return `Terminated: ${value} (${percentage}%)`;
                  }
                  
                  if (label === 'Other') {
                    return `Other: ${value} (${percentage}%)`;
                  }
                  
                  return `${label}: ${value} (${percentage}%)`;
                }
              }
            }
          },
          cutout: '65%'
        }
      });
      
    } catch (error) {
      console.error('❌ Error creating chart:', error);
    }
  },

  updateChart() {
    
    if (!this.chart) {
      this.initChart();
      return;
    }

    const activated = parseInt(this.el.dataset.activated) || 0;
    const blocked = parseInt(this.el.dataset.blocked) || 0;
    const delivered = parseInt(this.el.dataset.delivered) || 0;
    const terminated = parseInt(this.el.dataset.terminated) || 0;
    const other = parseInt(this.el.dataset.other) || 0;


    this.chart.data.datasets[0].data = [activated, blocked, delivered, terminated, other];
    this.chart.update('none');
    
  }
};

export const PoolConsumptionBarChart = {
  mounted() {
    this.chart = null;
    this.initChart();
  },

  updated() {
    this.updateChart();
  },

  destroyed() {
    if (this.chart) {
      this.chart.destroy();
    }
  },

  initChart() {
    const ctx = this.el.getContext('2d');
    
    const poolData = JSON.parse(this.el.dataset.pools || '[]');
    
    const labels = poolData.map(p => this.shortenOfferingName(p.name));
    const percentages = poolData.map(p => parseFloat(p.consumption_percentage) || 0);
    const colors = percentages.map(p => this.getBarColor(p));

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'Consumption %',
          data: percentages,
          backgroundColor: colors,
          borderColor: colors.map(c => this.darkenColor(c)),
          borderWidth: 2,
          borderRadius: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const pool = poolData[context.dataIndex];
                return [
                  `Consumption: ${context.parsed.x.toFixed(1)}%`,
                  `Consumed: ${this.formatDataSize(pool.volume_consumed)}`,
                  `Remaining: ${this.formatDataSize(pool.volume_in_pool)}`
                ];
              },
              title: (items) => {
                const pool = poolData[items[0].dataIndex];
                return pool.name;
              }
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: (value) => value + '%'
            },
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          y: {
            grid: {
              display: false
            },
            ticks: {
              font: {
                size: 11,
                weight: 'bold'
              }
            }
          }
        }
      }
    });
    
  },

  updateChart() {
    if (!this.chart) return;

    const poolData = JSON.parse(this.el.dataset.pools || '[]');
    const labels = poolData.map(p => this.shortenOfferingName(p.name));
    const percentages = poolData.map(p => parseFloat(p.consumption_percentage) || 0);
    const colors = percentages.map(p => this.getBarColor(p));

    this.chart.data.labels = labels;
    this.chart.data.datasets[0].data = percentages;
    this.chart.data.datasets[0].backgroundColor = colors;
    this.chart.data.datasets[0].borderColor = colors.map(c => this.darkenColor(c));
    this.chart.update('none');
  },

  shortenOfferingName(name) {
    if (!name) return 'Unknown';
    
    const match = name.match(/(\d+\s*KB|MB|GB).*?(\d+M|12M)/i);
    if (match) {
      return `${match[1]} - ${match[2]}`;
    }
    
    return name.length > 30 ? name.substring(0, 30) + '...' : name;
  },

  getBarColor(percentage) {
    if (percentage > 80) return '#EF4444';
    if (percentage > 60) return '#F59E0B';
    return '#3B82F6';
  },

  darkenColor(color) {
    const colors = {
      '#EF4444': '#DC2626',
      '#F59E0B': '#D97706',
      '#3B82F6': '#2563EB'
    };
    return colors[color] || color;
  },

  formatDataSize(kb) {
    if (!kb) return '0 KB';
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    if (kb < 1048576) return `${(kb / 1024).toFixed(2)} MB`;
    return `${(kb / 1048576).toFixed(2)} GB`;
  }
};